import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Especialidad } from 'src/app/models/Especialidad';
import { Prestacion } from 'src/app/models/Prestacion';
import { Servicio } from 'src/app/models/Servicio';
import { UsuarioService } from 'src/app/services/usuario.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-my-account-servicio',
    templateUrl: './my-account-servicio.component.html',
    styleUrls: ['./my-account-servicio.component.css'],
    standalone: false
})
export class MyAccountServicioComponent implements OnInit{
  private _router = inject(Router);
  private _aRoute = inject(ActivatedRoute);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);

  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  // estado
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // id (0 => agregar)
  id = Number(this._aRoute.snapshot.paramMap.get('id') ?? 0);

  operacion = signal<string>('Agregar ');
  botonOperacion = signal<string>('Agregar ');

  // en edicion solo se puede tener una prestacion; al agregar, varias a la vez
  readonly esAgregarMultiple = !this.id || this.id === 0;

  // combos
  especialidades = signal<Especialidad[]>([{ id: 0, x_descripcion: 'Seleccione', b_activo: true }]);
  prestaciones = signal<Prestacion[]>([{ id: 0, x_descripcion: 'Seleccione', b_activo: true }]);
  prestacionesDisponibles = computed<Prestacion[]>(() => this.prestaciones().filter(p => p.id !== 0));
  prestacionesSeleccionadas = signal<number[]>([]);
  intentoEnviar = signal<boolean>(false);
  especialidadSearch = signal<string>('');
  showEspecialidadesList = signal<boolean>(false);
  especialidadesFiltradas = computed<Especialidad[]>(() => {
    const term = normalizeText(this.especialidadSearch());

    return this.especialidades()
      .filter(e => e.id !== 0)
      .filter(e => e.b_activo !== false)
      .filter(e => normalizeText(e.x_descripcion).includes(term) || normalizeText(e.x_tags ?? '').includes(term))
      .sort((a, b) =>
        (a.x_descripcion ?? '').localeCompare(b.x_descripcion ?? '', 'es', {
          sensitivity: 'base'
        })
      );
  });

  botonLabel = computed<string>(() => {
    if (this.id && this.id !== 0) {
      return 'Actualizar Servicio';
    }

    const n = this.prestacionesSeleccionadas().length;
    return n > 1 ? `Agregar ${n} Servicios` : 'Agregar Servicio';
  });

  // form
  myAccountServicio = this._fb.group({
    especialidad: [0, [Validators.required, Validators.min(1)]],
    descripcion: ['', Validators.required],
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
        return;
      }

      // 1) cargar especialidades
      try {
        const resEsp = await firstValueFrom(this._usuarioService.getEspecialidades());
        this.especialidades.set([
          { id: 0, x_descripcion: 'Seleccione', b_activo: true },
          ...(resEsp as Especialidad[])
        ]);
      } catch (e: any) {
        console.error(e);
        this.error.set('Error al obtener las Especialidades');
      }

      // 2) editar / agregar
      if (this.id && this.id !== 0) {
        this.operacion.set('Editar ');
        this.botonOperacion.set('Actualizar ');

        try {
          const srv: any = await firstValueFrom(this._usuarioService.getServicio(this.id));

          if (!srv) {
            // No existe => no tiene sentido editar
            this.error.set('El servicio no existe o fue eliminado.');
            this._router.navigate(['/my-account-servicios']);
            return;
          }

          // cargar prestaciones de la especialidad del servicio
          await this.loadPrestacionesByEspecialidad(Number(srv.id_especialidad ?? 0));

          this.myAccountServicio.patchValue({
            especialidad: Number(srv.id_especialidad ?? 0),
            descripcion: String(srv.x_descripcion ?? ''),
          });

          const idPrestacion = Number(srv.id_prestacion ?? 0);
          this.prestacionesSeleccionadas.set(idPrestacion ? [idPrestacion] : []);

          this.especialidadSearch.set(this.getEspecialidadDescripcion());

        } catch (e: any) {
          console.error(e);
          this.error.set('Error al obtener los datos del Servicio');
        }
      } else {
        // agregar: defaults
        this.myAccountServicio.patchValue({ especialidad: 0 });
      }
    });
  }

  back() {
    this._location.back();
  }

  // importante: usar el ID real, NO selectedIndex
  async searchEspecialidad(event: Event): Promise<void> {
    const select = event.target as HTMLSelectElement;
    const idEspecialidad = Number(select.value || 0);
    await this.loadPrestacionesByEspecialidad(idEspecialidad);
  }

  getEspecialidadDescripcion(): string {
    const id = Number(this.myAccountServicio.controls.especialidad.value ?? 0);

    if (!id) return '';

    return this.especialidades()
      .find(e => Number(e.id) === id)
      ?.x_descripcion ?? '';
  }

  onEspecialidadInput(value: string): void {
    this.especialidadSearch.set(value);
    this.showEspecialidadesList.set(true);

    const idActual = Number(this.myAccountServicio.controls.especialidad.value ?? 0);
    const descripcionActual = this.getEspecialidadDescripcion();

    if (idActual && normalizeText(value) !== normalizeText(descripcionActual)) {
      this.myAccountServicio.patchValue({ especialidad: 0 });

      this.prestaciones.set([
        { id: 0, x_descripcion: 'Seleccione', b_activo: true }
      ]);
      this.prestacionesSeleccionadas.set([]);
    }
  }

  async selectEspecialidad(especialidad: Especialidad): Promise<void> {
    const idEspecialidad = Number(especialidad.id ?? 0);

    this.myAccountServicio.patchValue({ especialidad: idEspecialidad });

    this.especialidadSearch.set(especialidad.x_descripcion ?? '');
    this.showEspecialidadesList.set(false);

    await this.loadPrestacionesByEspecialidad(idEspecialidad);
  }

  private async loadPrestacionesByEspecialidad(idEspecialidad: number): Promise<void> {
    this.error.set('');
    this.prestacionesSeleccionadas.set([]);

    // si es 0 => limpiar prestaciones
    if (!idEspecialidad || idEspecialidad === 0) {
      this.prestaciones.set([{ id: 0, x_descripcion: 'Seleccione', b_activo: true }]);
      return;
    }

    try {
      const res = await firstValueFrom(this._usuarioService.getPrestacionesXEspecialidad(idEspecialidad));
      this.prestaciones.set([
        { id: 0, x_descripcion: 'Seleccione', b_activo: true },
        ...(res as Prestacion[])
      ]);
    } catch (e: any) {
      console.error(e);
      this.error.set('Error al obtener las Prestaciones');
      this.prestaciones.set([{ id: 0, x_descripcion: 'Seleccione', b_activo: true }]);
    }
  }

  isPrestacionSelected(idPrestacion: number): boolean {
    return this.prestacionesSeleccionadas().includes(idPrestacion);
  }

  togglePrestacion(idPrestacion: number): void {
    if (!this.esAgregarMultiple) {
      // en edicion: comportamiento tipo radio, una sola prestacion
      this.prestacionesSeleccionadas.set([idPrestacion]);
      return;
    }

    const actuales = this.prestacionesSeleccionadas();
    this.prestacionesSeleccionadas.set(
      actuales.includes(idPrestacion)
        ? actuales.filter(id => id !== idPrestacion)
        : [...actuales, idPrestacion]
    );
  }

  toggleSeleccionarTodasLasPrestaciones(): void {
    const disponibles = this.prestacionesDisponibles().map(p => p.id!);

    this.prestacionesSeleccionadas.set(
      this.prestacionesSeleccionadas().length === disponibles.length ? [] : disponibles
    );
  }

  async saveServicio(): Promise<void> {
    this.error.set('');
    this.intentoEnviar.set(true);

    if (this.myAccountServicio.invalid) {
      this.myAccountServicio.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    const v = this.myAccountServicio.value;
    const idEspecialidad = Number(v.especialidad ?? 0);
    const descripcion = String(v.descripcion ?? '').trim();
    const idsPrestaciones = this.prestacionesSeleccionadas();

    if (!idEspecialidad || idEspecialidad === 0) {
      this.error.set('Debe Seleccionar la Especialidad');
      return;
    }
    if (idsPrestaciones.length === 0) {
      this.error.set('Debe seleccionar al menos una Prestación');
      return;
    }
    if (!descripcion) {
      this.error.set('Debe Completar la Descripción');
      return;
    }

    await this._loadingService.run(async () => {
      // edicion: siempre una unica prestacion
      if (this.id && this.id !== 0) {
        const servicio: Servicio = {
          id: this.id,
          id_usuario: this._usuarioService.usuario().id,
          id_especialidad: idEspecialidad,
          id_prestacion: idsPrestaciones[0],
          x_descripcion: descripcion,
        };

        try {
          await firstValueFrom(this._usuarioService.updateServicio(servicio));
          await this._router.navigate(['/my-account-servicios']);
        } catch (e) {
          console.error(e);
          this.error.set('Error al actualizar el Servicio');
        }
        return;
      }

      // agregar: una prestacion por servicio, todas con la misma especialidad y descripcion
      const resultados = await Promise.allSettled(
        idsPrestaciones.map(idPrestacion => {
          const servicio: Servicio = {
            id_usuario: this._usuarioService.usuario().id,
            id_especialidad: idEspecialidad,
            id_prestacion: idPrestacion,
            x_descripcion: descripcion,
          };
          return firstValueFrom(this._usuarioService.createServicio(servicio));
        })
      );

      const idsFallidos = idsPrestaciones.filter((_, i) => resultados[i].status === 'rejected');

      if (idsFallidos.length === 0) {
        await this._router.navigate(['/my-account-servicios']);
        return;
      }

      resultados.forEach(r => {
        if (r.status === 'rejected') console.error(r.reason);
      });

      // dejar seleccionadas solo las que fallaron, para poder reintentar
      this.prestacionesSeleccionadas.set(idsFallidos);

      this.error.set(
        idsFallidos.length === idsPrestaciones.length
          ? 'No se pudo agregar ningún servicio. Intentá nuevamente.'
          : `Se agregaron ${idsPrestaciones.length - idsFallidos.length} de ${idsPrestaciones.length} servicios. Volvé a intentar con los restantes.`
      );
    });
  }
}
